import React, { useRef, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import { BarChart } from 'echarts/charts';
import {
  TooltipComponent,
  TitleComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../providers/AppProvider';

echarts.use([
  TooltipComponent,
  TitleComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  CanvasRenderer
]);

const getShiftChartOptions = (theme, getThemeColor, shiftData = {}) => {
  const shifts = Object.keys(shiftData || {}); // Shifts (A, B, C) pe axa X
  // const machines = Array.from(
  //   new Set(
  //     shifts.flatMap(shift => Object.keys(shiftData[shift])) // Mașini unice
  //   )
  // );

  const machines = Array.from(
    new Set(
      shifts
        .map(shift => Object.keys(shiftData[shift])) // Map each shift to its machine keys
        .reduce((acc, val) => acc.concat(val), []) // Flatten the resulting arrays
    )
  );

  // Culorile pentru bare
  const colors = {
    totalTime: theme === 'dark' ? `hsl(0, 70%, 50%)` : `hsl(0, 50%, 60%)`, // Reddish
    activeTime: theme === 'dark' ? `hsl(120, 70%, 40%)` : `hsl(120, 50%, 55%)`, // Greenish
    breakTime: theme === 'dark' ? `hsl(45, 85%, 50%)` : `hsl(45, 65%, 60%)` // Yellowish
  };

  // Pregătirea seriilor
  const series = [];

  machines.forEach(machine => {
    // Total Time
    series.push({
      name: 'Total Time',
      type: 'bar',
      stack: machine, // Grupat pe mașină
      data: shifts.map(shift => shiftData[shift]?.[machine]?.totalTime || 0),
      itemStyle: {
        color: colors.totalTime
      },
      label: {
        show: true,
        position: 'top',
        formatter: () => machine, // Afișează numele mașinii deasupra barei
        fontSize: 9,
        rotate: 90, // vertical — incap toate masinile fara suprapunere
        align: 'left',
        verticalAlign: 'middle',
        color: getThemeColor('gray-800')
      },
      barMaxWidth: 8
    });

    // Active Time
    series.push({
      name: 'Active Time',
      type: 'bar',
      stack: `${machine} ActiveBreak`, // Grupat cu Break Time
      data: shifts.map(shift => shiftData[shift]?.[machine]?.activeTime || 0),
      itemStyle: {
        color: colors.activeTime
      },
      label: {
        show: false // Fără etichete pentru acest bar
      },
      barMaxWidth: 8
    });

    // Break Time
    series.push({
      name: 'Break Time',
      type: 'bar',
      stack: `${machine} ActiveBreak`, // Grupat cu Active Time
      data: shifts.map(shift => shiftData[shift]?.[machine]?.breakTime || 0),
      itemStyle: {
        color: colors.breakTime
      },
      label: {
        show: false // Fără etichete pentru acest bar
      },
      barMaxWidth: 8
    });
  });

  return {
    tooltip: {
      trigger: 'item',
      formatter: ({ seriesName, value, name }) =>
        `<strong>${name}</strong><br>${seriesName}: ${value.toFixed(2)} hours`
    },
    legend: {
      data: ['Total Time', 'Active Time', 'Break Time'],
      textStyle: {
        color: getThemeColor('gray-800'),
        fontSize: 12
      },
      bottom: 0
    },
    xAxis: {
      type: 'category',
      data: shifts, // Shifts pe axa X
      axisLabel: {
        fontSize: 12,
        color: getThemeColor('gray-800')
      },
      axisLine: {
        lineStyle: {
          color: getThemeColor('gray-300')
        }
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 12,
        formatter: '{value} hours',
        color: getThemeColor('gray-800')
      },
      axisLine: {
        lineStyle: { color: getThemeColor('gray-300') }
      },
      splitLine: {
        show: true,
        lineStyle: { color: getThemeColor('gray-300') }
      }
    },
    series,
    grid: {
      left: '10%',
      right: '10%',
      bottom: '20%',
      top: '15%', // loc pentru numele rotite deasupra barelor
      containLabel: true
    },
    animation: true
  };
};

const ShiftTotalActiveBreakTime = ({ shiftData, title }) => {
  const chartRef = useRef(null);
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getShiftChartOptions(theme, getThemeColor, shiftData)}
          style={{ height: '400px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold'
  }
};

export default memo(ShiftTotalActiveBreakTime);
